import React from "react";
import { motion } from "framer-motion";
import { Play, ArrowUpRight, Youtube } from "lucide-react";
import { useLightbox } from "./VideoLightbox";

const PUBG_BG = "https://customer-assets.emergentagent.com/job_creator-website-1/artifacts/re3d5mxt_Arsov%20A.png";
const QR_CODE = "https://customer-assets.emergentagent.com/job_creator-website-1/artifacts/j6npg3pq_ARSOV%20yt%20%285%29%202.2.png";
const CHANNEL_URL = "https://www.youtube.com/channel/UCLpwX8aPs_ha-BC4bgQwVwg";

export default function Hero({ featured, stats, upcomingLive }) {
  const { open } = useLightbox();

  const handleWatch = (e) => {
    if (featured) {
      e.preventDefault();
      open(featured);
    }
  };

  const upcomingDate = upcomingLive?.published
    ? new Date(upcomingLive.published)
    : null;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden flex items-end pb-20 pt-32"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain md:bg-cover"
        style={{ backgroundImage: `url(${PUBG_BG})`, backgroundColor: "#0F1A3C" }}
      />
      {/* Overlays — lighter so the script logo stays readable */}
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/85 via-transparent to-[#050505]/60" />
      <div className="absolute inset-0 scanlines pointer-events-none" />

      {/* Top-left QR code — mirrors the script logo on the right */}
      <motion.a
        href={CHANNEL_URL}
        target="_blank"
        rel="noreferrer"
        data-testid="hero-qr-card"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="hidden lg:block absolute top-28 left-12 z-20 group"
      >
        <div className="relative bg-white p-3 w-[180px] shadow-[0_20px_60px_-15px_rgba(196,30,58,0.5)] transition-transform duration-500 group-hover:scale-[1.04]">
          <span className="absolute -top-2 -left-2 w-5 h-5 border-t-2 border-l-2 border-[#C41E3A]" />
          <span className="absolute -top-2 -right-2 w-5 h-5 border-t-2 border-r-2 border-[#C41E3A]" />
          <span className="absolute -bottom-2 -left-2 w-5 h-5 border-b-2 border-l-2 border-[#C41E3A]" />
          <span className="absolute -bottom-2 -right-2 w-5 h-5 border-b-2 border-r-2 border-[#C41E3A]" />
          <img
            src={QR_CODE}
            alt="Scan to subscribe to ARSOV on YouTube"
            className="w-full h-auto select-none"
            draggable={false}
          />
        </div>
        <p className="mt-3 font-mono-tech text-[10px] uppercase tracking-[0.3em] text-white/80 text-center flex items-center justify-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#C41E3A] animate-pulse" />
          Scan · Subscribe
        </p>
      </motion.a>

      {/* Diagonal red stripe */}
      <div className="absolute top-1/2 -left-4 w-2 h-44 bg-[#C41E3A] rotate-12 hidden md:block" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 w-full">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          data-testid="hero-eyebrow"
          className="text-xs uppercase tracking-[0.4em] text-[#C41E3A] mb-6 flex items-center gap-3"
        >
          <span className="w-10 h-px bg-[#C41E3A]" /> Gaming · Pov · Music · MTB · Shorts 
        </motion.p>

        {upcomingLive && (
          <motion.button
            type="button"
            onClick={() => open(upcomingLive)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-testid="hero-upcoming-live"
            className="inline-flex items-center gap-3 mb-6 border border-[#C41E3A]/60 bg-[#C41E3A]/10 hover:bg-[#C41E3A]/20 px-4 py-2 transition-colors group"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C41E3A] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C41E3A]" />
            </span>
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-white">
              Stream Scheduled
            </span>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/70 group-hover:text-white">
              {upcomingDate ? upcomingDate.toLocaleDateString(undefined, { month: "short", day: "2-digit" }) : ""} · {upcomingLive.title.slice(0, 32)}{upcomingLive.title.length > 32 ? "…" : ""}
            </span>
          </motion.button>
        )}

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          data-testid="hero-title"
          className="font-display text-[18vw] md:text-[14vw] lg:text-[12vw] leading-[0.85] tracking-tight uppercase text-white"
        >
          ARSOV
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-8 max-w-2xl"
        >
          <p className="text-white/80 text-base md:text-lg leading-relaxed">
            Battlegrounds, late-night drives, mountain trails. One channel,
            shot like a film. Strap in — every clip earns its frame.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={featured ? "#" : CHANNEL_URL}
            onClick={handleWatch}
            target={featured ? "_self" : "_blank"}
            rel="noreferrer"
            data-testid="hero-watch-latest-btn"
            className="group inline-flex items-center gap-3 bg-[#C41E3A] hover:bg-white text-white hover:text-[#C41E3A] px-7 py-4 text-xs uppercase tracking-[0.3em] font-semibold transition-colors duration-300"
          >
            <Play className="w-4 h-4 fill-current" />
            {featured ? "Watch Latest Drop" : "Watch Channel"}
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
          <a
            href={CHANNEL_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-subscribe-btn"
            className="inline-flex items-center gap-3 border border-white/30 hover:border-white text-white/90 hover:text-white px-7 py-4 text-xs uppercase tracking-[0.3em] font-semibold backdrop-blur-md transition-colors"
          >
            <Youtube className="w-4 h-4" /> Subscribe
          </a>
        </motion.div>

        {featured && (
          <motion.button
            type="button"
            onClick={() => open(featured)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            data-testid="hero-featured-card"
            className="mt-14 inline-flex items-start gap-4 max-w-md group text-left"
          >
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#C41E3A] mt-1 whitespace-nowrap">
              ⏵ Now Playing
            </span>
            <span className="text-white/90 text-sm leading-snug border-b border-white/10 group-hover:border-[#C41E3A] pb-2 line-clamp-2">
              {featured.title}
            </span>
          </motion.button>
        )}
      </div>

      {/* Bottom timeline */}
      <div className="absolute bottom-6 left-0 right-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between font-mono-tech text-[10px] uppercase tracking-[0.3em] text-white/40">
          <span>EST · MMXIX</span>
          <span className="hidden sm:inline">YT · @ARSOV_a</span>
          <span>SCROLL ↓</span>
        </div>
      </div>
    </section>
  );
}
